import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Box, DraftingCompass, Files, Lock, Plus, Printer } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { NewEngProjectDialog } from '@/components/engineering/NewEngProjectDialog';
import { AvatarStack, BudgetBar, EngStatusBadge, PersonChip, StatCard } from '@/components/engineering/EngParts';
import { useAuth } from '@/app/AuthProvider';
import { useEngProjects, type EngProjectListRow } from '@/features/engineering/hooks';
import {
  ENG_PIPELINE_STATUSES,
  ENG_STATUS_META,
  ENG_STATUS_OPTIONS,
  canCreateEngProject,
  isEngLockedStatus,
} from '@/features/engineering/model';
import { formatCurrency, formatDateOnly, formatRelative, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function EngineeringDashboardPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const { data: projects, isLoading } = useEngProjects({ includeArchived: showArchived });

  const rows = projects ?? [];
  const active = rows.filter((p) => !p.archived_at);
  const today = todayIsoDate();

  const totals = useMemo(() => {
    const live = active.filter((p) => !['cancelled'].includes(p.status));
    const inWork = live.filter((p) => ['concept', 'design', 'prototype', 'testing'].includes(p.status));
    const locked = live.filter((p) => isEngLockedStatus(p.status));
    const overdue = inWork.filter((p) => p.target_date && p.target_date < today);
    return {
      inWork: inWork.length,
      locked: locked.length,
      overdue: overdue.length,
      files: live.reduce((s, p) => s + p.fileCount, 0),
      prints: live.reduce((s, p) => s + p.printCount, 0),
      spent: live.reduce((s, p) => s + p.spentCents, 0),
      budget: live.reduce((s, p) => s + (p.budget_cents ?? 0), 0),
    };
  }, [active, today]);

  const pipeline = useMemo(
    () =>
      ENG_PIPELINE_STATUSES.map((status) => ({
        status,
        count: active.filter((p) => p.status === status).length,
      })),
    [active],
  );

  const columns = useMemo<ColumnDef<EngProjectListRow, unknown>[]>(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Code',
        size: 110,
        cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.code}</span>,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Project',
        size: 320,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className={cn('truncate font-medium text-foreground', row.original.archived_at && 'line-through')}>
              {row.original.name}
            </div>
            {row.original.description && (
              <div className="truncate text-xs text-muted-foreground">{row.original.description}</div>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <EngStatusBadge status={row.original.status} />,
      },
      {
        id: 'owner',
        accessorFn: (r) => r.owner?.full_name ?? r.owner?.email ?? '',
        header: 'Owner',
        size: 180,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <PersonChip person={row.original.owner} size="xs" />,
      },
      {
        id: 'team',
        accessorFn: (r) => r.eng_project_members.length,
        header: 'Team',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => <AvatarStack people={row.original.eng_project_members.map((m) => m.profiles)} />,
      },
      {
        id: 'target_date',
        accessorFn: (r) => r.target_date ?? '9999-12-31',
        header: 'Target',
        size: 130,
        cell: ({ row }) => {
          const p = row.original;
          if (!p.target_date) return <span className="text-muted-foreground">—</span>;
          const late = p.target_date < today && !isEngLockedStatus(p.status) && p.status !== 'cancelled';
          return (
            <span className={cn('whitespace-nowrap text-sm', late && 'font-medium text-fresh-salmon-dark')}>
              {formatDateOnly(p.target_date)}
            </span>
          );
        },
      },
      {
        id: 'files',
        accessorKey: 'fileCount',
        header: 'Files',
        size: 80,
        cell: ({ row }) => <span className="tabular-nums">{row.original.fileCount}</span>,
      },
      {
        id: 'prints',
        accessorKey: 'printCount',
        header: 'Prints',
        size: 80,
        cell: ({ row }) => <span className="tabular-nums">{row.original.printCount}</span>,
      },
      {
        id: 'spend',
        accessorKey: 'spentCents',
        header: 'Spend / budget',
        size: 180,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="min-w-[140px]">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="tabular-nums">{formatCurrency(p.spentCents, { whole: true })}</span>
                <span className="text-xs text-muted-foreground">
                  {p.budget_cents != null ? `of ${formatCurrency(p.budget_cents, { whole: true })}` : 'no budget'}
                </span>
              </div>
              <BudgetBar spentCents={p.spentCents} budgetCents={p.budget_cents} className="mt-1" />
            </div>
          );
        },
      },
      {
        id: 'updated_at',
        accessorKey: 'updated_at',
        header: 'Updated',
        size: 120,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">{formatRelative(row.original.updated_at)}</span>
        ),
      },
    ],
    [today],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'status', title: 'Status', options: ENG_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
      { columnId: 'owner', title: 'Owner' },
    ],
    [],
  );

  const canCreate = canCreateEngProject(role);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Engineering"
        title="Engineering projects"
        meta="Design files with revision history, 3D prints, costs and tasks — one place per part."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/engineering/prints">
                <Printer className="h-4 w-4" />
                3D print log
              </Link>
            </Button>
            {canCreate && (
              <Button onClick={() => setNewOpen(true)}>
                <Plus className="h-4 w-4" />
                New project
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="In development" value={totals.inWork} icon={DraftingCompass} hint="Concept → testing" />
        <StatCard
          label="Locked / in production"
          value={totals.locked}
          icon={Lock}
          hint="Design files frozen"
        />
        <StatCard
          label="Past target date"
          value={totals.overdue}
          icon={AlertTriangle}
          hint={totals.overdue > 0 ? 'Still in development' : 'Everything on schedule'}
          className={totals.overdue > 0 ? 'border-fresh-salmon/60' : undefined}
        />
        <StatCard
          label="Spend to date"
          value={formatCurrency(totals.spent, { whole: true })}
          icon={Box}
          hint={
            totals.budget > 0
              ? `${formatCurrency(totals.budget, { whole: true })} budgeted · ${totals.files} files · ${totals.prints} prints`
              : `${totals.files} files · ${totals.prints} prints`
          }
        />
      </div>

      {active.length > 0 && (
        <Card>
          <CardContent className="pt-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Pipeline</p>
              <p className="text-xs text-muted-foreground">Active projects by stage</p>
            </div>
            <ol className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {pipeline.map(({ status, count }) => {
                const meta = ENG_STATUS_META[status];
                return (
                  <li
                    key={status}
                    className={cn(
                      'rounded-lg border px-3 py-2',
                      count > 0 ? 'border-border bg-card' : 'border-dashed border-border/70 bg-transparent',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">{meta.shortLabel}</span>
                      {meta.locked && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />}
                    </div>
                    <p className={cn('mt-1 text-xl font-medium tabular-nums', count === 0 && 'text-muted-foreground/60')}>
                      {count}
                    </p>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable<EngProjectListRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            searchPlaceholder="Search projects by code, name or owner…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100]}
            onRowClick={(r) => navigate(`/engineering/${r.id}`)}
            rowClassName={(r) => (r.archived_at ? 'opacity-60' : undefined)}
            enableColumnReordering
            enableColumnResizing
            tableId="engineering-projects"
            toolbarSlot={
              <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 text-sm">
                <Checkbox checked={showArchived} onCheckedChange={setShowArchived} ariaLabel="Show archived projects" />
                Show archived
              </label>
            }
            emptyState={
              <EmptyState
                icon={DraftingCompass}
                title={showArchived ? 'No engineering projects yet' : 'No active engineering projects'}
                detail="Start a project to collect its CAD files, prints, costs and tasks."
                action={
                  canCreate ? (
                    <Button onClick={() => setNewOpen(true)}>
                      <Plus className="h-4 w-4" />
                      New project
                    </Button>
                  ) : undefined
                }
              />
            }
            mobileCard={{
              title: (r) => r.name,
              subtitle: (r) => <span className="font-mono text-[11px]">{r.code}</span>,
              dimmed: (r) => !!r.archived_at,
              badges: (r) => [
                <EngStatusBadge key="status" status={r.status} />,
                ...(r.target_date
                  ? [
                      <Badge key="target" variant="outline">
                        Target {formatDateOnly(r.target_date)}
                      </Badge>,
                    ]
                  : []),
              ],
              meta: (r) => [
                { label: 'Owner', value: r.owner?.full_name ?? r.owner?.email ?? '—' },
                { label: 'Files', value: String(r.fileCount) },
                { label: 'Prints', value: String(r.printCount) },
                { label: 'Spend', value: formatCurrency(r.spentCents, { whole: true }) },
              ],
            }}
          />
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Files className="h-3.5 w-3.5" />
          Tip: open a project’s Files tab to upload a whole folder — matching file names become new versions
          automatically.
        </p>
      )}

      <NewEngProjectDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
